import React, { useState, useEffect, useCallback } from "react";
import Modal from "../../components/Modal";
import { Button } from "../../components/Button";
import { Input } from "../../components/Input";
import api from "../../api/api";
import { toast } from "react-toastify";
import { knowAcces } from "../../utils/utils";
import { HiOutlineCheck } from "react-icons/hi2";
import { MdPercent } from "react-icons/md";
import { formatItalianNumber } from "../../utils/formatItalianNumber";

const fmt = (v) =>
  v != null
    ? formatItalianNumber(v, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : "—";

const formatMeseAnno = (value) => {
  const match = String(value || "").match(/^(\d{4})-(\d{2})/);
  return match ? `${match[2]}/${match[1]}` : "";
};

// Quando un nuovo periodo di sconto si sovrappone a uno gia' programmato nel
// futuro, calcola l'ultimo mese utile come data fine (il mese prima
// dell'inizio del periodo futuro piu' vicino) da proporre in automatico
// invece di limitarsi a bloccare il salvataggio.
const suggerisciMeseFineMassimo = (rows, meseInizio, excludeId = null) => {
  const inizioStr = `${meseInizio}-01`;
  const futuri = rows
    .filter((r) => (excludeId == null || String(r.id) !== String(excludeId)) && r.data_inizio > inizioStr)
    .map((r) => r.data_inizio)
    .sort();
  if (!futuri.length) return null;
  const [anno, mese] = futuri[0].split("-").map(Number);
  const prevAnno = mese === 1 ? anno - 1 : anno;
  const prevMese = mese === 1 ? 12 : mese - 1;
  return `${prevAnno}-${String(prevMese).padStart(2, "0")}`;
};

// Riga valida per il mese corrente tra uno storico di periodi (percentuale +
// data_inizio + data_fine opzionale = indeterminato, formato YYYY-MM-DD).
const trovaScontoAttivo = (rows) => {
  const oggi = new Date();
  const meseCorrente = `${oggi.getFullYear()}-${String(oggi.getMonth() + 1).padStart(2, "0")}-01`;
  return rows.find((r) => r.data_inizio <= meseCorrente && (!r.data_fine || r.data_fine >= meseCorrente)) || null;
};

const EDIT_DEFAULT = { id: null, percentuale: "", meseInizio: "", meseInizioOriginale: "", meseFine: "", note: "" };

// Default globale di sconto per i clienti iiPay (billing_client_discounts con
// codice_univoco_azienda NULL): si applica a tutti i clienti iiPay che non
// hanno un proprio sconto specifico configurato dal Listino Cliente (vedi
// box "Sconto cliente" in Listini.js, stesso meccanismo/API). Quando si
// applica/modifica il default, l'elenco clienti permette di deselezionare
// chi va escluso: per i deselezionati viene creata una riga a 0% (stesso
// meccanismo del cliente Zucchetti), su cui poi si puo' impostare uno
// sconto personalizzato dal Listino Cliente.
export const ScontoGlobaleIIpay = ({ clienti = [] }) => {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editRow, setEditRow] = useState(EDIT_DEFAULT);
  const [selezionati, setSelezionati] = useState(new Set());
  const [clientiSearch, setClientiSearch] = useState("");
  const [saving, setSaving] = useState(false);

  const isAdmin = knowAcces(["parc_admin"]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/billing/client-discounts/global/iipay");
      setRows(res.data || []);
    } catch {
      toast.error("Errore caricamento sconto globale iiPay");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const attivo = trovaScontoAttivo(rows);

  const openEdit = (row = null) => {
    const meseInizio = row?.data_inizio ? row.data_inizio.slice(0, 7) : "";
    setEditRow({
      id: row?.id ?? null,
      percentuale: row ? fmt(row.percentuale) : "",
      meseInizio,
      meseInizioOriginale: meseInizio,
      meseFine: row?.data_fine ? row.data_fine.slice(0, 7) : "",
      note: row?.note || "",
    });
    setSelezionati(new Set(clienti.map((c) => c.codice_univoco)));
    setClientiSearch("");
    setEditOpen(true);
  };

  const toggleCliente = (codice) => {
    setSelezionati((prev) => {
      const next = new Set(prev);
      if (next.has(codice)) next.delete(codice);
      else next.add(codice);
      return next;
    });
  };

  const clientiFiltrati = clienti.filter((c) =>
    !clientiSearch.trim() || String(c.ragione_sociale || "").toLowerCase().includes(clientiSearch.trim().toLowerCase())
  );

  const handleSave = async () => {
    const percentuale = Number(String(editRow.percentuale).replace(",", "."));
    if (!Number.isFinite(percentuale) || percentuale < 0 || percentuale > 100) {
      toast.warning("Indica una percentuale valida (0-100)");
      return;
    }
    if (!/^\d{4}-\d{2}$/.test(editRow.meseInizio)) {
      toast.warning("Indica il mese di inizio");
      return;
    }
    // Cambiare il mese di inizio rispetto a quello caricato apre un NUOVO
    // periodo (storicizzando quello attuale) invece di correggerlo in place:
    // in quel caso si crea sempre una riga nuova (POST), anche se la modale
    // e' stata aperta da "Modifica" - upsertPeriodoScontoCliente chiude gia'
    // da solo il periodo precedente ancora aperto.
    const correzioneInPlace = !!editRow.id && editRow.meseInizio === editRow.meseInizioOriginale;
    setSaving(true);
    try {
      const payload = {
        percentuale,
        data_inizio: editRow.meseInizio,
        data_fine: editRow.meseFine || null,
        note: editRow.note || null,
      };
      if (correzioneInPlace) {
        await api.put(`/billing/client-discounts/${editRow.id}`, payload);
      } else {
        await api.post("/billing/client-discounts", { ...payload, codice_univoco_azienda: null });
      }

      const esclusi = clienti.filter((c) => !selezionati.has(c.codice_univoco));
      if (esclusi.length) {
        const risultati = await Promise.allSettled(
          esclusi.map((c) =>
            api.post("/billing/client-discounts", {
              percentuale: 0,
              data_inizio: editRow.meseInizio,
              data_fine: editRow.meseFine || null,
              // Stesso testo usato da openEditScontoClienteEscludi in
              // Listini.js: stessa azione, stessa nota.
              note: "Escluso dal default globale iiPay",
              codice_univoco_azienda: c.codice_univoco,
            })
          )
        );
        const falliti = risultati.filter((r) => r.status === "rejected").length;
        if (falliti) {
          toast.warning(
            `${falliti} cliente/i non esclusi (hanno gia' una configurazione sconto in conflitto per questo periodo): sistemali dal Listino Cliente.`
          );
        }
      }

      await load();
      setEditOpen(false);
      toast.success("Sconto globale iiPay aggiornato");
    } catch (err) {
      if (err.response?.status === 409) {
        const suggerito = suggerisciMeseFineMassimo(rows, editRow.meseInizio, correzioneInPlace ? editRow.id : null);
        if (suggerito) {
          setEditRow((prev) => ({ ...prev, meseFine: suggerito }));
          toast.warning(
            `Il periodo si sovrappone a uno gia' programmato: ho impostato come fine ${formatMeseAnno(suggerito)}. Correggi se necessario e salva di nuovo, oppure elimina prima il periodo in conflitto dallo Storico.`
          );
          return;
        }
      }
      toast.error(err.response?.data?.message || "Errore salvataggio sconto globale");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row) => {
    const periodo = `${formatMeseAnno(row.data_inizio)}${row.data_fine ? ` - ${formatMeseAnno(row.data_fine)}` : " (indeterminato)"}`;
    if (!window.confirm(`Eliminare il periodo di sconto globale ${fmt(row.percentuale)}% (${periodo})? L'operazione non può essere annullata.`)) return;
    try {
      await api.delete(`/billing/client-discounts/${row.id}`);
      await load();
      toast.success("Periodo eliminato");
    } catch (err) {
      toast.error(err.response?.data?.message || "Errore eliminazione periodo");
    }
  };

  return (
    <>
      <Button
        className="edit-button"
        text="Sconto globale"
        type="button"
        icon={<MdPercent className="button-icon" />}
        onClick={() => setOpen(true)}
      />

      <Modal isOpen={open} closeButton={() => setOpen(false)} headerText="Sconto globale iiPay" size="medium">
        <div className="flex flex-col gap-3 p-2">
          <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
            <div className="text-xs uppercase text-gray-500">Attivo ora</div>
            <div className="font-medium text-gray-800">
              {attivo
                ? `${fmt(attivo.percentuale)}% dal ${formatMeseAnno(attivo.data_inizio)}${
                    attivo.data_fine ? ` al ${formatMeseAnno(attivo.data_fine)}` : " (indeterminato)"
                  }`
                : "Nessuno sconto globale attivo"}
            </div>
          </div>
          {isAdmin && (
            <Button
              className="principal-button"
              text={attivo ? "Modifica" : "Applica sconto globale"}
              type="button"
              onClick={() => openEdit(attivo)}
            />
          )}
          <div className="text-xs font-semibold uppercase text-gray-500">Storico</div>
          {loading ? (
            <div className="text-sm text-gray-500">Carico...</div>
          ) : rows.length ? (
            <table className="min-w-full border-collapse text-sm">
              <thead className="text-gray-500">
                <tr>
                  <th className="border-b px-2 py-1.5 text-right font-semibold">Percentuale</th>
                  <th className="border-b px-2 py-1.5 text-left font-semibold">Dal</th>
                  <th className="border-b px-2 py-1.5 text-left font-semibold">Al</th>
                  <th className="border-b px-2 py-1.5 text-left font-semibold">Note</th>
                  <th className="border-b px-2 py-1.5 text-left font-semibold"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id} className="border-b align-middle">
                    <td className="px-2 py-1.5 text-right font-semibold text-gray-800">{fmt(row.percentuale)}%</td>
                    <td className="px-2 py-1.5 text-gray-600">{formatMeseAnno(row.data_inizio)}</td>
                    <td className="px-2 py-1.5 text-gray-600">
                      {row.data_fine ? formatMeseAnno(row.data_fine) : "indeterminato"}
                    </td>
                    <td className="px-2 py-1.5 text-gray-600">{row.note || ""}</td>
                    <td className="px-2 py-1.5 text-right">
                      {isAdmin && (
                        <button
                          type="button"
                          onClick={() => handleDelete(row)}
                          className="rounded border border-red-200 bg-red-50 px-2 py-0.5 text-xs font-semibold text-red-700 hover:bg-red-100"
                        >
                          Elimina
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="text-sm text-gray-400">Nessuno sconto globale configurato.</div>
          )}
        </div>
      </Modal>

      <Modal
        isOpen={editOpen}
        closeButton={() => setEditOpen(false)}
        headerText={editRow.id ? "Modifica sconto globale" : "Applica sconto globale"}
        size="medium"
        leftButtons={
          <Button
            className="principal-button"
            text={saving ? "Salvo..." : "Salva"}
            type="button"
            icon={<HiOutlineCheck className="button-icon" />}
            onClick={handleSave}
            disabled={saving}
          />
        }
      >
        <div className="grid grid-cols-2 gap-3 p-2">
          <Input
            label="Percentuale (%)"
            type="text"
            placeholder="es. 5,00"
            value={editRow.percentuale}
            onChange={(e) => setEditRow((prev) => ({ ...prev, percentuale: e.target.value }))}
          />
          <Input
            label="Note"
            type="text"
            value={editRow.note}
            onChange={(e) => setEditRow((prev) => ({ ...prev, note: e.target.value }))}
          />
          <Input
            label="Dal (mese)"
            type="month"
            value={editRow.meseInizio}
            onChange={(e) => setEditRow((prev) => ({ ...prev, meseInizio: e.target.value }))}
          />
          <Input
            label="Al (mese, vuoto = indeterminato)"
            type="month"
            value={editRow.meseFine}
            onChange={(e) => setEditRow((prev) => ({ ...prev, meseFine: e.target.value }))}
          />
          <div className="col-span-2 flex flex-col gap-1">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold uppercase text-gray-500">
                Clienti inclusi ({selezionati.size}/{clienti.length})
              </span>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setSelezionati(new Set(clienti.map((c) => c.codice_univoco)))}
                  className="text-xs font-semibold text-blue-700 hover:underline"
                >
                  Tutti
                </button>
                <button
                  type="button"
                  onClick={() => setSelezionati(new Set())}
                  className="text-xs font-semibold text-blue-700 hover:underline"
                >
                  Nessuno
                </button>
              </div>
            </div>
            <Input
              type="text"
              placeholder="Cerca cliente..."
              value={clientiSearch}
              onChange={(e) => setClientiSearch(e.target.value)}
            />
            <div className="max-h-52 overflow-auto rounded border border-gray-200">
              {clientiFiltrati.map((c) => (
                <label
                  key={c.codice_univoco}
                  className="flex cursor-pointer items-center gap-2 border-b border-gray-100 px-2 py-1.5 text-sm last:border-b-0 hover:bg-gray-50"
                >
                  <input
                    type="checkbox"
                    checked={selezionati.has(c.codice_univoco)}
                    onChange={() => toggleCliente(c.codice_univoco)}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600"
                  />
                  <span className="truncate">{c.ragione_sociale}</span>
                </label>
              ))}
              {!clientiFiltrati.length && <div className="p-2 text-xs text-gray-400">Nessun cliente trovato.</div>}
            </div>
            <div className="text-xs text-gray-500">
              I clienti deselezionati non ricevono lo sconto globale (restano a 0%); potrai comunque assegnare loro
              uno sconto personalizzato dal Listino Cliente, come per Zucchetti.
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};
